import { useNavigate } from "react-router-dom";
import { Alert, Empty, Modal, Space, Statistic, Table, Button } from "antd";
import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";

import type { DashboardDrilldownRow } from "../../../services/Report/ReportApis";

export type ChartDrilldownSummary = {
  label: string;
  count: number;
  percent: number;
  extra?: string;
};

interface IModalProps {
  show: boolean;
  onClose: () => void;
  title: string;
  summary: ChartDrilldownSummary | null;
  rows: DashboardDrilldownRow[];
  loading: boolean;
  apiError?: string | null;
}

const csvEscape = (value: any) => {
  const text = String(value ?? "");
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const ChartDrilldownModal: React.FC<IModalProps> = ({
  show,
  onClose,
  title,
  summary,
  rows,
  loading,
  apiError,
}) => {
  const navigate = useNavigate();

  const handleView = (row: DashboardDrilldownRow) => {
    navigate(row.ViewPath || `/members/${row.MemberID ?? row.id}`);
  };

  const handleExport = () => {
    const headers = [
      "ID Number",
      "Policy Number",
      "Name",
      "Surname",
      "Group",
      "Product",
      "Date Registered",
      "Premium",
    ];
    const lines = [
      headers.join(","),
      ...rows.map((row) =>
        [
          row.IdNumber,
          row.PolicyNumber,
          row.Name,
          row.Surname,
          row.GroupName,
          row.ProductName,
          row.DateRegistered,
          row.Premium,
        ]
          .map(csvEscape)
          .join(",")
      ),
    ];
    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${title.replace(/\s+/g, "-").toLowerCase()}-drilldown.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const columns: any[] = [
    {
      title: "ID Number",
      dataIndex: "IdNumber",
      render: (v: string) => v || "-",
    },
    {
      title: "Policy Number",
      dataIndex: "PolicyNumber",
      render: (v: string) => v || "-",
    },
    {
      title: "Name",
      dataIndex: "Name",
      render: (v: string) => v || "-",
    },
    {
      title: "Surname",
      dataIndex: "Surname",
      render: (v: string) => v || "-",
    },
    {
      title: "Group",
      dataIndex: "GroupName",
      render: (v: string) => v || "-",
    },
    {
      title: "Product",
      dataIndex: "ProductName",
      render: (v: string) => v || "-",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, row: DashboardDrilldownRow) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleView(row)}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <Modal
      open={show}
      title={title}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnClose
    >
      {summary && (
        <Space size="large" style={{ marginBottom: 16 }} wrap>
          <Statistic title="Bucket" value={summary.label} />
          <Statistic title="Count" value={summary.count} />
          <Statistic
            title="% of chart"
            value={summary.percent}
            precision={1}
            suffix="%"
          />
          {summary.extra ? (
            <Statistic title="Detail" value={summary.extra} />
          ) : null}
        </Space>
      )}

      {apiError && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Unable to load member drilldown"
          description={apiError}
        />
      )}

      {!loading && rows.length === 0 && !apiError ? (
        <Empty description="No member rows for this bucket yet" />
      ) : (
        <>
          {rows.length > 0 && (
            <div style={{ marginBottom: 12, textAlign: "right" }}>
              <Button icon={<DownloadOutlined />} onClick={handleExport}>
                Export CSV
              </Button>
            </div>
          )}
          <Table
            rowKey={(r) =>
              String(r.MemberID ?? r.id ?? r.PolicyNumber ?? Math.random())
            }
            columns={columns}
            dataSource={rows}
            loading={loading}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 700 }}
            locale={{
              emptyText: apiError ? "Unable to load data" : "No data",
            }}
          />
        </>
      )}
    </Modal>
  );
};

export default ChartDrilldownModal;
